// The admin console's health tabs (.design-specs/admin-console.plan.md §5–6): anomalies (the sweep's episodes and
// the new-connection watch list) and push (who can be reached, and how sends have gone). Read-only (R1).
import { Card, DataTable, EmptyState, Tag, type DataTableColumn } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminAnomalies,
  useAdminPush,
  type AdminAnomalyRow,
} from "../../app/hooks/useAdmin.js";
import { ago, exact, platformLabel } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

/** What each rule means, in a sentence a reviewer can act on. */
const RULE_LABELS: Record<string, string> = {
  "new-connection": "New connection",
  "source-switch": "Songstats disagrees with the band's own account",
  "counter-backwards": "Running total went backwards",
  "carried-forward": "Running total frozen while the platform moved",
  "fabricated-zero": "Reading dropped to zero",
  "zero-baseline": "Percentage shown off a zero baseline",
};

const SURFACE_LABELS: Record<string, string> = {
  trending: "Trending pill",
  hero: "Spotify hero",
  snapshots: "Stored series",
};

function subjectLabel(subject: string | null | undefined): string {
  if (!subject) return "—";
  const parts = subject.split(":").filter((p) => p !== "platform");
  const [platform, ...metric] = parts;
  return `${platformLabel(platform)} · ${metric.join(" ").replace(/_/g, " ")}`;
}

const ANOMALY_COLUMNS: DataTableColumn<AdminAnomalyRow>[] = [
  { key: "band", header: "Band", cell: (a) => <strong>{a.bandName}</strong> },
  {
    key: "rule",
    header: "Rule",
    cell: (a) => (
      <Tag tone={a.rule === "new-connection" ? "neutral" : "danger"} size="sm">
        {RULE_LABELS[a.rule ?? ""] ?? a.rule}
      </Tag>
    ),
  },
  { key: "subject", header: "Number", cell: (a) => subjectLabel(a.subject) },
  { key: "surface", header: "Where", cell: (a) => SURFACE_LABELS[a.surface ?? ""] ?? a.surface },
  { key: "detail", header: "Detail", cell: (a) => a.detail ?? "—" },
  { key: "first", header: "Since", cell: (a) => ago(a.firstSeenOn) },
];

const CLEARED_COLUMNS: DataTableColumn<AdminAnomalyRow>[] = [
  ...ANOMALY_COLUMNS.slice(0, 4),
  { key: "cleared", header: "Cleared", cell: (a) => ago(a.clearedOn) },
];

export function AnomaliesTab() {
  const q = useAdminAnomalies();

  if (q.isLoading) return <Card title="Anomalies" subtitle="Reading…" />;
  if (q.isError) {
    return (
      <Card title="Anomalies">
        <p className={styles.error}>{errorMessage(q.error, "Could not read anomalies.")}</p>
      </Card>
    );
  }
  const panel = q.data;
  if (!panel) return null;
  const open = panel.open ?? [];
  const watch = panel.watch ?? [];
  const cleared = panel.recentlyCleared ?? [];

  // "The sweep has not run" and "nothing is wrong" are different sentences.
  const sweptLine = panel.lastSweepOn
    ? `Last sweep activity ${ago(panel.lastSweepOn)}. The sweep runs every few hours over every active band.`
    : "The sweep has not recorded anything on this lane yet. It runs every few hours in the worker.";

  return (
    <div className={styles.stack}>
      <Card title="Open anomalies" subtitle={`Numbers a band is being shown, or soon will be, that look wrong. ${sweptLine}`}>
        {open.length === 0 ? (
          <EmptyState title="Nothing open" description="No rule is firing for any band." />
        ) : (
          <DataTable columns={ANOMALY_COLUMNS} rows={open} rowKey={(a) => a.anomalyId ?? ""} pageSize={25} label="Open anomalies pages" />
        )}
      </Card>

      <Card
        title="New-connection watch list"
        subtitle="Every change a band was shown on a platform connected in the last 14 days, with the readings behind it. Not an alarm — a review queue, and the evidence for deciding later what counts as implausible."
      >
        {watch.length === 0 ? (
          <EmptyState title="Nothing to watch" description="No recently connected platform has shown a band a change." />
        ) : (
          <DataTable columns={ANOMALY_COLUMNS} rows={watch} rowKey={(a) => a.anomalyId ?? ""} pageSize={25} label="Watch list pages" />
        )}
      </Card>

      {cleared.length > 0 && (
        <Card title="Recently cleared" subtitle="Episodes that stopped firing in the last 14 days.">
          <DataTable columns={CLEARED_COLUMNS} rows={cleared} rowKey={(a) => a.anomalyId ?? ""} pageSize={25} label="Cleared pages" />
        </Card>
      )}
    </div>
  );
}

export function PushTab() {
  const q = useAdminPush();

  if (q.isLoading) return <Card title="Push" subtitle="Reading…" />;
  if (q.isError) {
    return (
      <Card title="Push">
        <p className={styles.error}>{errorMessage(q.error, "Could not read push health.")}</p>
      </Card>
    );
  }
  const panel = q.data;
  if (!panel) return null;
  const devices = panel.devices ?? [];
  const outcomes = panel.outcomes ?? [];

  return (
    <div className={styles.stack}>
      {panel.authFailureRecently && (
        <Card title="Apple is rejecting this lane's push key">
          <p className={styles.error}>
            At least one send since yesterday came back <code>InvalidProviderToken</code> or <code>ExpiredProviderToken</code>.
            That fails every push to every device, not one phone. Check the lane's APNs key.
          </p>
        </Card>
      )}

      <Card title="Registered devices" subtitle="Phones that have registered for push, by platform and APNs environment.">
        {devices.length === 0 ? (
          <EmptyState title="No devices" description="No phone has registered for push on this lane." />
        ) : (
          <DataTable
            columns={[
              { key: "platform", header: "Platform", cell: (d) => d.platform },
              { key: "env", header: "APNs environment", cell: (d) => d.appEnv },
              {
                key: "state",
                header: "State",
                cell: (d) => (
                  <Tag tone={d.enabled ? "success" : "neutral"} size="sm">
                    {d.enabled ? "enabled" : "disabled"}
                  </Tag>
                ),
              },
              { key: "devices", header: "Devices", align: "end", cell: (d) => exact(d.devices) },
              { key: "people", header: "People", align: "end", cell: (d) => exact(d.people) },
            ]}
            rows={devices}
            rowKey={(d) => `${d.platform}/${d.appEnv}/${d.enabled}`}
          />
        )}
      </Card>

      <Card title="Sends" subtitle={`Every push the worker sent in the last ${panel.outcomeDays} days, by outcome and Apple's reason.`}>
        {outcomes.length === 0 ? (
          <EmptyState title="No sends recorded" description="Nothing has been sent since the tally started, or the worker is not running." />
        ) : (
          <DataTable
            columns={[
              { key: "day", header: "Day", cell: (o) => o.day },
              {
                key: "outcome",
                header: "Outcome",
                cell: (o) => (
                  <Tag tone={o.outcome === "Sent" ? "success" : o.outcome === "TransientFailure" ? "warning" : "danger"} size="sm">
                    {o.outcome}
                  </Tag>
                ),
              },
              { key: "reason", header: "Reason", cell: (o) => o.reason || "—" },
              { key: "count", header: "Count", align: "end", cell: (o) => exact(o.count) },
            ]}
            rows={outcomes}
            rowKey={(o) => `${o.day}/${o.outcome}/${o.reason}`}
            pageSize={25}
            label="Sends pages"
          />
        )}
      </Card>
    </div>
  );
}
